/** @typedef {{ id: string, label: string, requiresDetail?: boolean }} ChoiceOption */

function isNumericLike(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return true;
  const s = String(value ?? '').trim();
  return s !== '' && !Number.isNaN(Number(s)) && /^-?\d+(\.\d+)?$/.test(s);
}

function unwrapOptionObject(opt) {
  if (!opt || typeof opt !== 'object') return opt;
  const nested =
    opt.option ??
    opt.answer_option ??
    opt.choice_option ??
    opt.choice ??
    opt.question_option;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return {
      ...nested,
      requires_input: opt.requires_input ?? nested.requires_input,
      requires_detail: opt.requires_detail ?? nested.requires_detail,
      has_input: opt.has_input ?? nested.has_input,
    };
  }
  return opt;
}

/**
 * Resolve canonical `{ id, label }` from one API option object.
 * @param {unknown} raw
 * @returns {{ id: string, label: string, requiresDetail: boolean } | null}
 */
export function resolveChoiceOptionFromApi(raw) {
  const opt = unwrapOptionObject(raw);
  if (typeof opt === 'string') {
    const label = opt.trim();
    if (!label) return null;
    return { id: label, label, requiresDetail: false };
  }
  if (!opt || typeof opt !== 'object') return null;

  const requiresDetail = Boolean(
    opt.requires_input || opt.requires_detail || opt.has_input
  );

  const explicitIdRaw =
    opt.id ?? opt.pk ?? opt.option_id ?? opt.value_id ?? opt.uuid ?? opt.key;
  const explicitId =
    explicitIdRaw != null && String(explicitIdRaw).trim() !== ''
      ? String(explicitIdRaw).trim()
      : '';

  const labelFromFields = [
    opt.label,
    opt.text,
    opt.name,
    opt.title,
    opt.display_label,
    opt.option_label,
    opt.option_text,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find((s) => s && !isNumericLike(s));

  const valueStr =
    opt.value != null && String(opt.value).trim() !== '' ? String(opt.value).trim() : '';

  // API shape `{ id: "<uuid>", value: "0" }` — id for save, value for display (incl. "0", "1-2").
  if (explicitId && valueStr) {
    return {
      id: explicitId,
      label: labelFromFields || valueStr,
      requiresDetail,
    };
  }

  let id = explicitId;
  let label = labelFromFields || '';

  if (!id && valueStr) {
    if (isNumericLike(valueStr)) {
      id = valueStr;
    } else if (!label) {
      label = valueStr;
      id = label;
    }
  }

  if (!label && valueStr && !isNumericLike(valueStr)) {
    label = valueStr;
  }

  if (!id && label) id = label;
  if (!label && id) label = id;
  if (!id) return null;

  return { id, label: label || id, requiresDetail };
}

/**
 * Normalize API options to `{ id, label }` for BE `options_answer_id` validation.
 * @param {unknown[]} rawOptions
 * @returns {ChoiceOption[]}
 */
export function normalizeChoiceOptionsFromApi(rawOptions) {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions.map(resolveChoiceOptionFromApi).filter(Boolean);
}

/** True when options include real DB ids (not plain label strings). */
export function rawOptionsHaveAuthoritativeIds(rawOptions) {
  if (!Array.isArray(rawOptions) || !rawOptions.length) return false;
  return rawOptions.some((raw) => {
    if (typeof raw === 'string') return false;
    const opt = unwrapOptionObject(raw);
    if (!opt || typeof opt !== 'object') return false;
    if (
      opt.id != null ||
      opt.pk != null ||
      opt.option_id != null ||
      opt.value_id != null ||
      opt.uuid != null
    ) {
      return String(opt.id ?? opt.pk ?? opt.option_id ?? opt.value_id ?? opt.uuid).trim() !== '';
    }
    const valueField = opt.value;
    if (valueField == null || !isNumericLike(valueField)) return false;
    const label = String(opt.label ?? opt.text ?? opt.name ?? '').trim();
    return label !== '' && label !== String(valueField).trim();
  });
}

export function isOptionBasedQuestionType(rawType) {
  const t = String(rawType || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  return t === 'choice' || t === 'multiple-choice' || t.includes('multiple');
}

export function isYesNoInputQuestionType(rawType) {
  const t = String(rawType || '')
    .toLowerCase()
    .replace(/[_\s]+/g, '-');
  return t.includes('yes') && t.includes('no') && t.includes('input');
}

/** Default Yes/No pair when the questions API omits options (legacy / dev only). */
export function defaultYesNoChoiceOptions() {
  return [
    { id: 'yes', label: 'Ja', requiresDetail: true },
    { id: 'no', label: 'Nee', requiresDetail: false },
  ];
}

const YES_LIKE_PATTERN = /^(yes|ja|y|true|sim|oui)$/i;

/**
 * When the API omits `requires_input`, infer detail on the affirmative option (Ja/Yes).
 * @param {ChoiceOption[]} choices
 * @returns {ChoiceOption[]}
 */
export function inferYesNoDetailOnChoices(choices) {
  if (!Array.isArray(choices) || choices.length < 2) return choices || [];
  if (choices.some((c) => c.requiresDetail)) return choices;
  const yesLike = choices.find(
    (c) => YES_LIKE_PATTERN.test(String(c.label || '').trim()) || YES_LIKE_PATTERN.test(String(c.id || '').trim())
  );
  if (yesLike) {
    return choices.map((c) =>
      c.id === yesLike.id ? { ...c, requiresDetail: true } : c
    );
  }
  return [{ ...choices[0], requiresDetail: true }, ...choices.slice(1)];
}

/** Whether this step should POST `options_answer_id` (BE array / option-based validation). */
export function stepUsesOptionsAnswerId(step) {
  if (!step) return false;
  if (step.yesNoUsesOptionIds) return true;
  if (step.usesOptionsAnswerId) return true;
  if (step.optionBased || step.multi) return true;
  const validationType = String(step.validation?.type || '').toLowerCase();
  return validationType === 'array';
}

function findChoiceByValue(choices, value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const byId = choices.find((c) => c.id === s);
  if (byId) return byId;
  const lower = s.toLowerCase();
  return (
    choices.find((c) => c.label === s) ||
    choices.find((c) => c.label.toLowerCase() === lower) ||
    null
  );
}

/**
 * @param {string} value - stored option id or legacy label
 * @param {ChoiceOption[]} choiceOptions
 * @returns {string}
 */
export function mapSingleValueToOptionId(value, choiceOptions) {
  const match = findChoiceByValue(choiceOptions || [], value);
  return match ? match.id : String(value ?? '').trim();
}

/**
 * Whether the follow-up text field should show for the current Yes/No selection.
 * @param {{ choiceOptions?: ChoiceOption[], detailTriggerOptionIds?: string[], detailTriggerValues?: string[] }} step
 * @param {unknown} selectedValue
 */
export function stepDetailInputVisible(step, selectedValue) {
  const sel = String(selectedValue ?? '').trim();
  if (!sel) return false;
  const choices = step.choiceOptions || [];
  const selLower = sel.toLowerCase();

  const byId = choices.find((c) => c.id === sel);
  if (byId) return Boolean(byId.requiresDetail);

  const byLabel = choices.find((c) => c.label.toLowerCase() === selLower);
  if (byLabel) return Boolean(byLabel.requiresDetail);

  const triggerIds = step.detailTriggerOptionIds || [];
  if (triggerIds.some((id) => String(id).toLowerCase() === selLower)) return true;

  const triggerLabels = step.detailTriggerValues || [];
  return triggerLabels.some((t) => String(t).toLowerCase() === selLower);
}

export function resolveStepMaxSelections(question, validation) {
  const fromField =
    Number(question?.max_selections) ||
    Number(question?.maxSelections);
  const fromRules = Number(validation?.max);
  if (Number.isFinite(fromField) && fromField > 0) return fromField;
  if (Number.isFinite(fromRules) && fromRules > 0) return fromRules;
  return undefined;
}

export function maxSelectionLimitMessage(max) {
  if (!Number.isFinite(max) || max <= 0) return null;
  if (max === 1) return 'Selecteer maximaal één optie.';
  return `Selecteer maximaal ${max} opties.`;
}

/**
 * Map prefill values (ids or legacy labels) to canonical option ids for a step.
 * @param {unknown[]} rawList
 * @param {{ choiceOptions?: ChoiceOption[] }} step
 * @returns {string[]}
 */
export function mapPrefillValuesToOptionIds(rawList, step) {
  const choices = step?.choiceOptions || [];
  const out = [];
  for (const item of rawList || []) {
    const s = String(item ?? '').trim();
    if (!s) continue;
    const match = findChoiceByValue(choices, s);
    out.push(match ? match.id : s);
  }
  return out;
}

/**
 * Map in-app answer value(s) to canonical option ids before POST.
 * @param {unknown} value - scalar or string[]
 * @param {{ choiceOptions?: ChoiceOption[] }} step
 * @returns {string[]}
 */
export function resolveOptionIdsForSubmit(value, step) {
  const arr = Array.isArray(value)
    ? value
    : value != null && String(value).trim() !== ''
      ? [value]
      : [];
  return mapPrefillValuesToOptionIds(
    arr.filter((x) => x !== undefined && x !== null && String(x).trim() !== ''),
    step
  );
}

/**
 * Build one save row for a Yes/No + detail step.
 * @param {object} step
 * @param {unknown} main
 * @param {unknown} detailRaw
 * @returns {{ question_id: number, answer?: string, options_answer_id?: string[] }}
 */
export function buildYesNoWithTextareaSaveRow(step, main, detailRaw) {
  const qid = step?.questionId;
  const mainStr = String(main ?? '').trim();
  const d = String(detailRaw ?? '').trim();
  const selectedId = mapSingleValueToOptionId(mainStr, step?.choiceOptions);
  const selectionForDetail = selectedId || mainStr;
  const needsDetail = stepDetailInputVisible(step, selectionForDetail);

  if (stepUsesOptionsAnswerId(step) && (step?.choiceOptions?.length ?? 0) >= 2) {
    const row = {
      question_id: qid,
      options_answer_id: selectedId ? [selectedId] : [],
    };
    if (selectedId) {
      if (d && needsDetail) row.answer = d;
    } else {
      row.answer = '';
    }
    return row;
  }

  let answer = mainStr;
  if (mainStr && needsDetail && d) {
    answer = `${mainStr}\n\n${d}`;
  }
  return { question_id: qid, answer: answer || '' };
}
